import React, { useMemo, useState } from "react";
import CustomButton from "components/Common/ui/CustomButton";
import CustomModal from "components/Common/CustomModal";
import { UpdateChangesTable } from "./auditLog/UpdateChangesTable";
import { StudentImportResults } from "./auditLog/StudentImportResults";
import { CreateChangesTable } from "./auditLog/CreateChangesTable";
import { DeletedChangesTable } from "./auditLog/DeletedChangesTable";
import { isCreatePayload, flattenAfter, normalizeUpdateChanges } from "utils/auditLogUtils";
import { useTranslation } from "react-i18next";

interface ChangesButtonProps {
  changes?: Record<string, any>;
  action?: string;          // "create" | "update" | "delete"
  entity?: string;          // e.g., "StudentImport"
  record?: any;             // resolved record from backend (used for delete)
  entityId?: string;        // optional (not needed here)
  buttonText?: string;      // override
}

const isDeleteAction = (action?: string) => action === "delete";

const ChangesButton: React.FC<ChangesButtonProps> = ({
  changes,
  action,
  entity,
  record,
  buttonText,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const isStudentImport = entity === "StudentImport" && isCreatePayload(changes);
  const createMode = !isStudentImport && isCreatePayload(changes);
  const deleteMode = isDeleteAction(action);

  const createRows = useMemo(
    () => (createMode ? flattenAfter(changes!.after) : []),
    [createMode, changes]
  );

  const updateRows = useMemo(
    () => (!createMode && !isStudentImport && !deleteMode ? normalizeUpdateChanges(changes) : []),
    [createMode, isStudentImport, deleteMode, changes]
  );

  const modalTitle =
    isStudentImport ? t("view_import_results")
    : createMode ? t("created_values")
    : deleteMode ? t("deleted_values")
    : t("updated_values");

  const btnText =
    buttonText ||
    (isStudentImport ? t("view_import_results")
    : createMode ? t("view_created_data")
    : deleteMode ? t("view_deleted_data")
    : t("view_changes"));

  return (
    <>
      <CustomButton size="sm" onClick={() => setOpen(true)}>
        <i className="ti ti-edit me-2" />
        {btnText}
      </CustomButton>

      <CustomModal
        title={modalTitle}
        isOpen={open}
        onClose={() => setOpen(false)}
        saveText={t("close") || "Close"}
      >
        {isStudentImport ? (
          <StudentImportResults after={(changes?.after || {})} />
        ) : createMode ? (
          <CreateChangesTable rows={createRows} after={changes?.after} />
        ) : deleteMode ? (
          <DeletedChangesTable record={record} />
        ) : (
          <UpdateChangesTable rows={updateRows} />
        )}
      </CustomModal>
    </>
  );
};

export default ChangesButton;
